"use client";

import { useQuery } from "@apollo/client/react";
import { type PlainDate, toPlainDateString } from "@sa-nu/shared-utils/date";

import { graphql } from "../../../../__generated__/gql";
import type { RoomTypeCalendarAvailabilityQuery } from "../../../../__generated__/gql/graphql";

// 検索カレンダー（DateFilterPopup）用クエリ。RoomTypeBrowse と同じネスト構造・絞り込み引数を
// 再利用するが、選択状況によらず常にカレンダー描画範囲全体の空き・価格が要るため、
// RoomTypeBrowse とは別クエリとして定義する（RoomTypeBrowse の nights は日程フィルタ有効時の
// 選択範囲しか取得せずペイロード肥大化を避けている）。
// availableAccommodations/fromPrice は検索カレンダーで使わないため選択しない。
const RoomTypeCalendarAvailabilityQueryDocument = graphql(`
  query RoomTypeCalendarAvailability(
    $provider: AccommodationReservationProviderInput!
    $areaIds: [ID!]
    $facilitySlugs: [String!]
    $evChargerAvailable: Boolean
    $architectureTypes: [String!]
    $largeOnly: Boolean
    $firstNight: PlainDate
    $lastNight: PlainDate
  ) {
    areasForReservation(provider: $provider, areaIds: $areaIds) {
      id
      slug
      sitesForReservation(provider: $provider) {
        id
        roomTypesForReservation(
          provider: $provider
          facilitySlugs: $facilitySlugs
          evChargerAvailable: $evChargerAvailable
          architectureTypes: $architectureTypes
          largeOnly: $largeOnly
        ) {
          id
          availabilities(
            firstNight: $firstNight
            lastNight: $lastNight
            provider: $provider
            evChargerAvailable: $evChargerAvailable
          ) {
            nights {
              night
              isAvailable
              price
              maxConsecutiveNights
            }
          }
        }
      }
    }
  }
`);

export type SearchCalendarArea =
  RoomTypeCalendarAvailabilityQuery["areasForReservation"][number];
export type SearchCalendarSite =
  SearchCalendarArea["sitesForReservation"][number];
export type SearchCalendarRoomType =
  SearchCalendarSite["roomTypesForReservation"][number];

export function useSearchCalendarAvailability({
  stayTicketUnitId,
  areaIds,
  facilitySlugs,
  evChargerAvailable,
  architectureTypes,
  largeOnly,
  firstNight,
  lastNight,
  skip: skipOverride = false,
}: {
  stayTicketUnitId: string | null;
  areaIds: string[];
  facilitySlugs: string[];
  evChargerAvailable: boolean;
  architectureTypes: string[];
  largeOnly: boolean;
  firstNight: PlainDate;
  lastNight: PlainDate;
  skip?: boolean;
}) {
  const { data, loading, error } = useQuery(
    RoomTypeCalendarAvailabilityQueryDocument,
    {
      variables: {
        provider: {
          providerType: "StayTicket" as const,
          providerStayTicketUnitId: stayTicketUnitId ?? "",
        },
        // 空配列・false は BE に「フィルタなし」として伝えるため null に変換する
        areaIds: areaIds.length > 0 ? areaIds : null,
        facilitySlugs: facilitySlugs.length > 0 ? facilitySlugs : null,
        evChargerAvailable: evChargerAvailable ? true : null,
        architectureTypes:
          architectureTypes.length > 0 ? architectureTypes : null,
        largeOnly: largeOnly ? true : null,
        firstNight: toPlainDateString(firstNight),
        lastNight: toPlainDateString(lastNight),
      },
      skip: !stayTicketUnitId || skipOverride,
      fetchPolicy: "cache-first",
    },
  );

  const areas = data?.areasForReservation ?? [];

  return {
    areas,
    loading,
    error,
  };
}
